import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import s from './styles.module.scss'

import DepositSection from './deposit-section/DepositSection'
import { getMinDepositRate } from './deposit-section/utils/get-min-deposit-rate'
import Footer from './footer/Footer'
import Information from './information/Information'
import TotalQuotationAmounts from './total-quotation-amounts/TotalQuotationAmounts'
import { IDepositFormValues } from '../owner-can-confirm-booking/owner-can-confirm-booking-modal/OwnerCanConfirmBookingModal'
import { getFileProps, onUploadFile } from '../../utils/utils'
import {
  ClientProposalOwnerUploadedQuotationInputDto,
  CompanyInformationClientProposalFragment,
} from '../../../../../../../lib/graphql'
import { errorToast } from '../../../../../../../utils'
import {
  CLIENT_PROPOSAL_SERVICE_PROVIDER_OWNER_FEES_RATE,
  multiplyByFactor,
} from '../../../../../../../utils/prices'
import UploadField from '../../../../../../atoms/fields/upload-field/UploadField'
import { Modal } from '../../../../../../molecules'

export interface IUploadQuotationProps {
  handleQuotationFormData?: (formData: FormData) => void
  handleSpecialConditionsFormData?: (formData: FormData) => void
  quotationUrl?: string
  setQuotationUrl?: React.Dispatch<React.SetStateAction<string | undefined>>
  specialConditionsUrl?: string
  setSpecialConditionsUrl?: React.Dispatch<
    React.SetStateAction<string | undefined>
  >
}

interface IModalLoadQuotation {
  isOpenModal: boolean
  setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>
  isModalQuotation?: boolean
  uploadQuotationData?: IUploadQuotationProps
  clientProposal: CompanyInformationClientProposalFragment
  ownerUploadedQuotation: {
    callback: (args: {
      clientProposalId: string
      ownerUploadedQuotationInputs: ClientProposalOwnerUploadedQuotationInputDto
    }) => void
    isLoading: boolean
  }
}

const UploadQuotationModal = ({
  isOpenModal,
  setIsOpenModal,
  uploadQuotationData,
  ownerUploadedQuotation,
  clientProposal,
}: IModalLoadQuotation) => {
  const intl = useIntl()
  const {
    quotationUrl,
    setQuotationUrl,
    specialConditionsUrl,
    setSpecialConditionsUrl,
  } = uploadQuotationData ?? {}
  const fileProps = getFileProps({ clientProposal })

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [quotationFileName, setQuotationFileName] = React.useState<
    string | undefined
  >(undefined)
  const [specialConditionsFileName, setSpecialConditionsFileName] =
    React.useState<string | undefined>(
      specialConditionsUrl && fileProps.entityName === 'house'
        ? `${intl.formatMessage({ id: 'ui.UploadQuotationFiles.prefixCGVTitle', defaultMessage: 'CGV -' })} ${fileProps.title}`
        : undefined,
    )

  const closeModal = () => {
    setQuotationUrl && setQuotationUrl(undefined)
    !specialConditionsUrl &&
      setSpecialConditionsUrl &&
      setSpecialConditionsUrl(undefined)
    setIsOpenModal(false)
  }

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid },
  } = useForm<
    Pick<IDepositFormValues, 'depositRate'> & {
      amountHT: string
      amountTTC: string
      isConfirmedCGU: boolean
      isConfirmAvailability: boolean
    }
  >({
    mode: 'all',
    defaultValues: {
      depositRate: '',
      amountHT: '',
      amountTTC: '',
      isConfirmedCGU: false,
      isConfirmAvailability: false,
    },
  })

  if (!clientProposal) {
    throw new Error('Client proposal is required')
  }

  const onSubmit = async () => {
    if (!quotationUrl || !specialConditionsUrl) {
      errorToast([
        intl.formatMessage({
          id: 'ui.UploadQuotationModal.uploadQuote',
          defaultMessage: 'Veuillez charger un devis',
        }),
      ])
      return
    }
    const data: ClientProposalOwnerUploadedQuotationInputDto = {
      depositRate: multiplyByFactor(
        parseFloat(
          getValues('depositRate') ||
            getMinDepositRate(clientProposal.startDate).toString(),
        ),
      ),
      totalAmountHT: multiplyByFactor(getValues('amountHT')),
      totalAmountTTC: multiplyByFactor(getValues('amountTTC')),
      quotationFile: { url: quotationUrl, fileName: quotationFileName ?? '' },
      specialConditionsFile: {
        url: specialConditionsUrl,
        fileName: specialConditionsFileName ?? '',
      },
    }

    await ownerUploadedQuotation.callback({
      clientProposalId: clientProposal._id,
      ownerUploadedQuotationInputs: data,
    })
    closeModal()
  }

  return (
    <Modal
      isOpen={isOpenModal}
      closeModal={closeModal}
      title={intl.formatMessage({
        id: 'io.UploadQuotationModal.modalTitle',
        defaultMessage: 'Charger un devis',
      })}
      className={s.modal}
      scrollbarStyle={{ padding: '20px 30px ' }}
      footerStyle={{ boxSizing: 'border-box' }}
      footer={{
        button: {
          label: intl.formatMessage({
            id: 'ui.UploadQuotationModal.sendQuote',
            defaultMessage: 'Envoyer mon devis et mes CGV',
          }),
          fullWidth: true,
          onClick: handleSubmit(onSubmit),
          disabled: !isValid,
          isLoading: ownerUploadedQuotation.isLoading || isLoading,
        },
      }}
    >
      <Information
        ownerFees={
          clientProposal.house?.fees?.ownerFeesRate ??
          CLIENT_PROPOSAL_SERVICE_PROVIDER_OWNER_FEES_RATE
        }
      />

      <div className='mt-5 flex flex-col gap-4'>
        <UploadField
          label={intl.formatMessage({
            id: 'ui.UploadQuotationFiles.uploadQuote',
            defaultMessage: 'Charger un devis - Obligatoire',
          })}
          onClear={(field) => {
            field.onChange(null)
            setQuotationFileName(undefined)
            setQuotationUrl && setQuotationUrl(undefined)
          }}
          onFileChange={(e) =>
            onUploadFile({
              event: e,
              onUploadFile: uploadQuotationData?.handleQuotationFormData,
              setFileName: setQuotationFileName,
            })
          }
          fileName={quotationFileName}
          url={quotationUrl}
          name='quotation'
          control={control}
          required={true}
        />

        <UploadField
          label={intl.formatMessage({
            id: 'ui.UploadQuotationFiles.uploadCGV',
            defaultMessage: 'Charger mes CGV',
          })}
          onClear={(field) => {
            field.onChange(null)
            setSpecialConditionsFileName(undefined)
            setSpecialConditionsUrl && setSpecialConditionsUrl(undefined)
          }}
          onFileChange={(e) =>
            onUploadFile({
              event: e,
              onUploadFile:
                uploadQuotationData?.handleSpecialConditionsFormData,
              setFileName: setSpecialConditionsFileName,
            })
          }
          url={specialConditionsUrl}
          fileName={specialConditionsFileName}
          name='specialConditions'
          control={control}
          required={true}
        />
      </div>

      <TotalQuotationAmounts
        control={control}
        currency={clientProposal.house?.currencyArea.currency}
        amountHT={getValues('amountHT')}
        amountTTC={getValues('amountTTC')}
      />

      <DepositSection
        control={control}
        startDate={clientProposal?.startDate}
        depositRate={getValues('depositRate')}
      />

      <Footer
        control={control}
        clientRequestId={clientProposal.clientRequestId}
        setIsLoading={setIsLoading}
        clientProposalId={clientProposal._id}
      />
    </Modal>
  )
}

export default UploadQuotationModal
